import { combineReducers } from '@reduxjs/toolkit';
import { all, fork } from 'redux-saga/effects';

import { IConfigState } from '../types';
import { agencyReducer } from './agency/reducer';
import AgencySaga from './agency/saga';
import { IAgenciesState } from './agency/types';
import { authReducer } from './auth/reducer';
import AuthSaga from './auth/saga';
import { AuthActionTypes, AuthState } from './auth/types';
import { configReducer } from './config/reducer';
import ConfigSaga from './config/saga';
import { dashboardReducer } from './dashboard/reducer';
import DashboardSaga from './dashboard/saga';
import { DashboardState } from './dashboard/types';
import { dataReducer } from './data/reducer';
import DataSaga from './data/saga';
import { IDataState } from './data/types';
import { programmableTagsReducer } from './programmable_tags/reducer';
import ProgrammableTagSaga from './programmable_tags/saga';
import { IProgrammableTagState } from './programmable_tags/types';
import { regionConfigDashboardReducer } from './region_config_dashboard/reducer';
import RegionConfigDashboardSaga from './region_config_dashboard/saga';
import { IRegionConfigsState } from './region_config_dashboard/types';
import StudentUserSaga from './studentUsers/saga';
import { uiReducer } from './ui/reducer';
import { UIState } from './ui/types';
import { userReducer } from './user/reducer';
import UserSaga from './user/saga';
import { IUserState } from './user/types';

import { FetchError } from 'shared-components';
import { websocketReducer, WebsocketState } from 'websocket-utility';
import { applyReducer, IApplyState } from './apply/reducer';
import ApplicantSaga from './apply/saga';
import { applyConfigReducer } from './apply_config/reducer';
import ApplyConfigSaga from './apply_config/saga';
import { IApplyConfigState } from './apply_config/types';
import { errorReducer } from './error/reducer';
import { IErrorState } from './error/types';
import { eventTypesReducer } from './event_types/reducer';
import EventTypesSaga from './event_types/saga';
import { IEventTypesState } from './event_types/types';
import { filtersReducer } from './filters/reducer';
import { IFiltersState } from './filters/types';
import { offersDashboardReducer } from './offers-dashboard/reducer';
import OffersDashboardSaga from './offers-dashboard/saga';
import { studentReducer } from './student/reducer';
import StudentSaga from './student/saga';
import { StudentState } from './student/types';
import { studentUserReducer } from './studentUsers/reducer';
import { IStudentUserState } from './studentUsers/types';
import { OffersDashboardState } from './offers-dashboard/types';

// The top-level state object
export interface ApplicationState {
  config: IConfigState;
  applyConfig: IApplyConfigState;
  agencies: IAgenciesState;
  regionConfigs: IRegionConfigsState;
  eventTypes: IEventTypesState;
  programmableTags: IProgrammableTagState;
  auth: AuthState;
  ui: UIState;
  dashboard: DashboardState;
  offersDashboard: OffersDashboardState;
  data: IDataState;
  user: IUserState;
  studentUsers: IStudentUserState;
  filters: IFiltersState;
  websocket: WebsocketState;
  student: StudentState;
  error: IErrorState;
  apply: IApplyState;
}

export const createRootReducer = () => {
  const appReducer = combineReducers<ApplicationState>({
    config: configReducer,
    applyConfig: applyConfigReducer,
    regionConfigs: regionConfigDashboardReducer,
    eventTypes: eventTypesReducer,
    agencies: agencyReducer,
    programmableTags: programmableTagsReducer,
    auth: authReducer,
    ui: uiReducer,
    dashboard: dashboardReducer,
    offersDashboard: offersDashboardReducer,
    data: dataReducer,
    user: userReducer,
    studentUsers: studentUserReducer,
    filters: filtersReducer,
    websocket: websocketReducer,
    student: studentReducer,
    error: errorReducer,
    apply: applyReducer,
  });

  const rootReducer = (state: ApplicationState | undefined, action: any) => {
    if (action.type === AuthActionTypes.COGNITO_LOGOUT_SUCCESS) {
      const clearedState: ApplicationState = appReducer(undefined, action);
      const newState = {
        ...clearedState,
        ui: {
          ...clearedState.ui,
          isLoading: false,
        },
      };
      // Do not modify router reducer.
      // if (state?.router) {
      //   newState = {
      //     ...newState,
      //     router: state.router
      //   };
      // }
      return newState;
    }

    if (action.type.includes('_FAILURE')) {
      const error = action.payload;
      if (error instanceof FetchError) {
        // Handle error state updates directly in the reducer
        // instead of dispatching actions to avoid circular dependency
        const newState = appReducer(state, action);
        switch (error.status) {
          case 403:
            return {
              ...newState,
              error: {
                ...newState.error,
                error403: true,
              },
            };
          case 404:
            return {
              ...newState,
              error: {
                ...newState.error,
                error404: true,
              },
            };
          default:
            return {
              ...newState,
              error: {
                ...newState.error,
                error500: true,
              },
            };
        }
      }
    }

    return appReducer(state, action);
  };

  return rootReducer;
};

// Here we use `redux-saga` to trigger actions asynchronously. `redux-saga` uses something called a
// "generator function", which you can read about here:
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/function*
export function* rootSaga() {
  yield all([fork(ConfigSaga)]);
  yield all([fork(RegionConfigDashboardSaga)]);
  yield all([fork(EventTypesSaga)]);
  yield all([fork(AgencySaga)]);
  yield all([fork(ProgrammableTagSaga)]);
  yield all([fork(AuthSaga)]);
  yield all([fork(DashboardSaga)]);
  yield all([fork(DataSaga)]);
  yield all([fork(UserSaga)]);
  yield all([fork(StudentUserSaga)]);
  yield all([fork(ApplicantSaga)]);

  yield all([fork(ApplyConfigSaga)]);
  yield all([fork(OffersDashboardSaga)]);

  // student
  // yield all([fork(ConfigSaga)]);
  // yield all([fork(ProgrammableTagSaga)]);
  // yield all([fork(AuthSaga)]);
  yield all([fork(StudentSaga)]);
  // yield all([fork(DashboardSaga)]);
  // yield all([fork(DataSaga)]);
}
